(function () {
  'use strict';

  var MOBILE_MQ = '(max-width: 699px)';
  var REDUCED_MOTION_MQ = '(prefers-reduced-motion: reduce)';

  function getScrollY() {
    return window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
  }

  function ProcessSteps(root) {
    this.root = root;
    this.section = root.closest('.shopify-section');
    this.activeIndex = 0;
    this.linePartial = 0;
    this.isMobile = window.matchMedia(MOBILE_MQ).matches;
    this.reducedMotion = window.matchMedia(REDUCED_MOTION_MQ).matches;
    this.observers = [];
    this.abortController = new AbortController();
    this.rafPending = false;
    this.measureQueued = false;
    this.isClickNavigating = false;
    this.onResize = this.onResize.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onSliderScroll = this.onSliderScroll.bind(this);
    this.onWindowScroll = this.onWindowScroll.bind(this);
    this.onLayoutChange = this.onLayoutChange.bind(this);
  }

  ProcessSteps.prototype.init = function () {
    this.scrollZone = this.root.querySelector('.process-steps__scroll-zone');
    this.desktopEl = this.root.querySelector('.process-steps__desktop');
    this.contentViewport = this.root.querySelector('.process-steps__desktop .process-steps__content-viewport');
    this.contentCol = this.root.querySelector('.process-steps__desktop .process-steps__content-col');
    this.stepsEl = this.root.querySelector('.process-steps__desktop .process-steps__steps');
    this.stepElements = Array.from(this.root.querySelectorAll('.process-steps__desktop .process-steps__step'));
    this.dotButtons = Array.from(this.root.querySelectorAll('.process-steps__desktop .process-steps__dot-btn'));
    this.mobileDotButtons = Array.from(this.root.querySelectorAll('.process-steps__mobile-dot-btn'));
    this.mobileTimeline = this.root.querySelector('.process-steps__mobile-timeline');
    this.mobileTimelineTrack = this.root.querySelector('.process-steps__mobile-timeline-track');
    this.mobileTimelineItems = Array.from(this.root.querySelectorAll('.process-steps__mobile-timeline-item'));
    this.imageLayers = Array.from(this.root.querySelectorAll('.process-steps__desktop .process-steps__image-layer'));
    this.slider = this.root.querySelector('.process-steps__slider');
    this.slides = this.slider ? Array.from(this.slider.querySelectorAll('.process-steps__slide')) : [];
    this.stepCount = this.stepElements.length || this.slides.length;
    this.maxInternalScroll = 0;
    this.maxZonePageScroll = 0;
    this.pinHeight = 0;
    this.zoneStartScroll = 0;
    this.stepOffsets = [];
    this.percentRatio = 1;
    this.visibleStepCount = 1;
    this.scrubStepCount = 1;
    this.desktopScrollMode = this.root.getAttribute('data-desktop-scroll') || 'scrub';
    this.stickyNeeded = false;

    this.bindEvents();

    if (this.isMobile) {
      this.initMobile();
    } else {
      this.initDesktop();
    }

    this.setActiveStep(0, { instant: true, fromClick: false });
  };

  ProcessSteps.prototype.isStickyImageMode = function () {
    return this.desktopScrollMode === 'sticky_image';
  };

  ProcessSteps.prototype.destroy = function () {
    this.abortController.abort();
    this.observers.forEach(function (obs) {
      obs.disconnect();
    });
    this.observers = [];
    clearTimeout(this.clickNavTimer);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('load', this.onLayoutChange);
    if (this.slider) {
      this.slider.removeEventListener('scroll', this.onSliderScroll);
    }
  };

  ProcessSteps.prototype.bindEvents = function () {
    var signal = this.abortController.signal;
    var self = this;

    this.dotButtons.forEach(function (btn, index) {
      btn.addEventListener('click', function (event) {
        event.preventDefault();
        self.handleDotClick(index);
      }, { signal: signal });
    });

    this.mobileDotButtons.forEach(function (btn) {
      btn.addEventListener('click', function (event) {
        event.preventDefault();
        var index = parseInt(btn.dataset.stepTrigger, 10);
        if (!isNaN(index)) {
          self.scrollToSlide(index);
        }
      }, { signal: signal });
    });

    window.addEventListener('resize', this.onResize, { signal: signal });
    window.addEventListener('scroll', this.onWindowScroll, { passive: true, signal: signal });
    document.addEventListener('scroll', this.onWindowScroll, { passive: true, signal: signal });
    window.addEventListener('load', this.onLayoutChange, { signal: signal });
    this.root.addEventListener('keydown', this.onKeyDown, { signal: signal });

    if (this.slider) {
      this.slider.addEventListener('scroll', this.onSliderScroll, { passive: true, signal: signal });
    }

    if (this.stepsEl && typeof ResizeObserver !== 'undefined') {
      var resizeObserver = new ResizeObserver(function () {
        if (!self.isMobile) {
          self.scheduleMeasureDesktop();
        }
      });
      resizeObserver.observe(this.stepsEl);
      if (this.contentViewport) {
        resizeObserver.observe(this.contentViewport);
      }
      this.observers.push(resizeObserver);
    }

    this.root.querySelectorAll('.process-steps__img').forEach(function (img) {
      if (img.complete) return;
      img.addEventListener('load', function () {
        self.scheduleMeasureDesktop();
      }, { once: true, signal: signal });
    });
  };

  ProcessSteps.prototype.getStickyTop = function () {
    var styles = getComputedStyle(this.root);
    var value = styles.getPropertyValue('--ps-sticky-top').trim();
    var parsed = parseFloat(value);
    if (!isNaN(parsed) && parsed > 0) return parsed;

    var header = parseFloat(styles.getPropertyValue('--header-height')) || 0;
    var announcement = parseFloat(styles.getPropertyValue('--announcement-bar-height')) || 0;
    // Fallback a variables del tema en :root
    var rootStyles = getComputedStyle(document.documentElement);
    if (!header) header = parseFloat(rootStyles.getPropertyValue('--header-height')) || 0;
    if (!announcement) announcement = parseFloat(rootStyles.getPropertyValue('--announcement-bar-height')) || 0;
    return header + announcement;
  };

  ProcessSteps.prototype.scheduleMeasureDesktop = function () {
    if (this.isMobile || this.measureQueued) return;

    var self = this;
    this.measureQueued = true;
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        self.measureQueued = false;
        if (!self.isMobile) {
          self.measureDesktop();
          self.syncDesktopFromPage();
        }
      });
    });
  };

  ProcessSteps.prototype.measureDesktop = function () {
    if (!this.desktopEl || !this.scrollZone || !this.stepsEl) return false;

    var stickyTop = this.getStickyTop();
    this.pinHeight = window.innerHeight - stickyTop;

    if (!this.pinHeight) {
      this.scrollZone.removeAttribute('data-ps-ready');
      return false;
    }

    this.stepOffsets = this.stepElements.map(function (step) {
      return step.offsetTop;
    });

    this.visibleStepCount = this.stepCount;
    this.scrubStepCount = this.stepCount;
    this.maxInternalScroll = 0;

    if (this.isStickyImageMode()) {
      return this.measureStickyImageDesktop();
    }

    this.maxZonePageScroll = this.stepCount > 1
      ? (this.stepCount - 1) * this.pinHeight
      : this.pinHeight * 0.35;

    this.percentRatio = this.pinHeight > 0
      ? (this.maxZonePageScroll + this.pinHeight) / this.pinHeight
      : 1;

    var percentDesktop = this.percentRatio * 100;
    var contentHeight = Math.max(this.desktopEl.offsetHeight, this.pinHeight);

    this.scrollZone.style.height = (this.maxZonePageScroll + contentHeight) + 'px';
    this.scrollZone.style.setProperty('--percent-desktop', percentDesktop + '%');
    this.scrollZone.style.setProperty('--percent-desktop-ratio', String(this.percentRatio));
    this.scrollZone.style.setProperty('--scroll-offset', '0px');
    this.root.style.setProperty('--percent-desktop', percentDesktop + '%');
    this.root.style.setProperty('--percent-desktop-ratio', String(this.percentRatio));
    this.root.style.setProperty('--scroll-offset', '0px');
    this.scrollZone.classList.toggle('has-scroll', this.maxZonePageScroll > 0);
    this.root.classList.remove('is-sticky-needed');
    this.stickyNeeded = false;

    this.updateZoneStartScroll();
    this.scrollZone.setAttribute('data-ps-ready', 'true');
    return true;
  };

  ProcessSteps.prototype.measureStickyImageDesktop = function () {
    this.scrollZone.style.height = '';
    this.setScrollState(0, 0);

    // Medir offsets del texto en layout natural (antes del pin)
    this.root.classList.remove('is-sticky-needed');

    var contentEl = this.contentCol || this.stepsEl;
    var contentHeight = contentEl ? contentEl.scrollHeight : this.desktopEl.offsetHeight;

    this.stepOffsets = this.stepElements.map(function (step) {
      return step.offsetTop;
    });

    var contentOverflow = Math.max(0, contentHeight - this.pinHeight);
    var stepTravel = this.stepCount > 1
      ? (this.stepCount - 1) * Math.min(this.pinHeight * 0.45, 360)
      : Math.min(this.pinHeight * 0.35, 240);

    // Siempre activo: imagen fija + texto con scroll
    this.maxInternalScroll = Math.max(contentOverflow, stepTravel);
    this.stickyNeeded = true;
    this.root.classList.add('is-sticky-needed');

    this.maxZonePageScroll = this.maxInternalScroll;
    this.percentRatio = this.pinHeight > 0
      ? (this.maxZonePageScroll + this.pinHeight) / this.pinHeight
      : 1;

    var percentDesktop = this.percentRatio * 100;
    this.scrollZone.style.height = (this.maxZonePageScroll + this.pinHeight) + 'px';
    this.scrollZone.style.setProperty('--percent-desktop', percentDesktop + '%');
    this.scrollZone.style.setProperty('--percent-desktop-ratio', String(this.percentRatio));
    this.root.style.setProperty('--percent-desktop', percentDesktop + '%');
    this.root.style.setProperty('--percent-desktop-ratio', String(this.percentRatio));
    this.scrollZone.classList.add('has-scroll');

    this.setScrollState(0, 0);
    this.updateZoneStartScroll();
    this.scrollZone.setAttribute('data-ps-ready', 'true');
    return true;
  };

  ProcessSteps.prototype.setScrollState = function (offset, progress) {
    if (!this.scrollZone) return;

    var clampedProgress = Math.min(1, Math.max(0, progress));
    var useOffset = this.isStickyImageMode()
      ? Math.max(0, offset) + 'px'
      : '0px';

    this.scrollZone.style.setProperty('--scroll-offset', useOffset);
    this.scrollZone.style.setProperty('--scroll-progress', String(clampedProgress));
    this.root.style.setProperty('--scroll-offset', useOffset);
    this.root.style.setProperty('--scroll-progress', String(clampedProgress));
  };

  ProcessSteps.prototype.getStepProgress = function (index) {
    if (this.stepCount <= 1) return 0;
    return Math.min(1, Math.max(0, index / (this.stepCount - 1)));
  };

  ProcessSteps.prototype.updateZoneStartScroll = function () {
    if (!this.scrollZone) return;

    var stickyTop = this.getStickyTop();
    var zoneRect = this.scrollZone.getBoundingClientRect();
    this.zoneStartScroll = getScrollY() + zoneRect.top - stickyTop;
  };

  ProcessSteps.prototype.getStickyImageProgressForStep = function (index) {
    if (this.maxInternalScroll <= 0) return 0;

    var targetOffset = this.stepOffsets[index] || 0;
    if (targetOffset <= 0 && index > 0) {
      return this.getStepProgress(index);
    }
    return Math.min(1, Math.max(0, targetOffset / this.maxInternalScroll));
  };

  ProcessSteps.prototype.clearDesktopScrollVars = function () {
    if (!this.scrollZone) return;

    this.scrollZone.style.height = '';
    this.scrollZone.removeAttribute('data-ps-ready');
    this.scrollZone.classList.remove('is-pinned', 'has-scroll');
    this.root.classList.remove('is-sticky-needed');
    this.stickyNeeded = false;

    ['--percent-desktop', '--percent-desktop-ratio', '--scroll-offset', '--scroll-progress', '--scrollbar-offset'].forEach(function (name) {
      this.scrollZone.style.removeProperty(name);
      this.root.style.removeProperty(name);
    }, this);
  };

  ProcessSteps.prototype.initDesktop = function () {
    var self = this;

    if (!this.measureDesktop()) {
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          self.measureDesktop();
          self.syncDesktopFromPage();
        });
      });
      return;
    }

    this.syncDesktopFromPage();
    setTimeout(function () {
      self.scheduleMeasureDesktop();
    }, 250);
  };

  ProcessSteps.prototype.initMobile = function () {
    this.clearDesktopScrollVars();
    this.syncMobileTimeline();
    this.onSliderScroll();
  };

  ProcessSteps.prototype.onLayoutChange = function () {
    if (this.isMobile) return;

    if (this.measureDesktop()) {
      this.syncDesktopFromPage();
    }
  };

  ProcessSteps.prototype.onWindowScroll = function () {
    if (this.isMobile || this.rafPending) return;

    var self = this;
    this.rafPending = true;
    requestAnimationFrame(function () {
      self.rafPending = false;
      self.syncDesktopFromPage();
    });
  };

  ProcessSteps.prototype.getScrollProgress = function () {
    if (!this.scrollZone || this.maxZonePageScroll <= 0) return 0;

    var stickyTop = this.getStickyTop();
    var zoneRect = this.scrollZone.getBoundingClientRect();

    if (zoneRect.top > stickyTop) {
      return 0;
    }

    if (zoneRect.bottom <= stickyTop + this.pinHeight) {
      return 1;
    }

    var scrolledInZone = -zoneRect.top + stickyTop;
    return Math.min(1, Math.max(0, scrolledInZone / this.maxZonePageScroll));
  };

  ProcessSteps.prototype.isInPinZone = function () {
    if (!this.scrollZone || this.maxZonePageScroll <= 0) return false;

    var stickyTop = this.getStickyTop();
    var zoneRect = this.scrollZone.getBoundingClientRect();
    return zoneRect.top <= stickyTop && zoneRect.bottom > stickyTop + this.pinHeight;
  };

  ProcessSteps.prototype.syncDesktopFromPage = function () {
    if (!this.scrollZone || !this.desktopEl || this.isClickNavigating) return;

    if (!this.scrollZone.getAttribute('data-ps-ready')) {
      this.measureDesktop();
    }

    if (this.isStickyImageMode()) {
      this.syncStickyImageFromPage();
      return;
    }

    var stickyTop = this.getStickyTop();
    var zoneRect = this.scrollZone.getBoundingClientRect();
    var progress = this.getScrollProgress();
    var inZone = this.isInPinZone();

    if (zoneRect.top > stickyTop) {
      this.updateZoneStartScroll();
    }

    this.scrollZone.classList.toggle('is-pinned', inZone);
    this.setScrollState(0, progress);
    this.updateFromDesktopScroll(progress);
  };

  ProcessSteps.prototype.syncStickyImageFromPage = function () {
    if (!this.scrollZone || !this.desktopEl || this.isClickNavigating) return;

    var stickyTop = this.getStickyTop();
    var zoneRect = this.scrollZone.getBoundingClientRect();
    var progress = this.getScrollProgress();
    var inZone = this.isInPinZone();

    if (zoneRect.top > stickyTop) {
      this.updateZoneStartScroll();
    }

    var offset = progress * this.maxInternalScroll;
    this.scrollZone.classList.toggle('is-pinned', inZone);
    this.setScrollState(offset, progress);

    var probe = offset + Math.min(120, this.pinHeight * 0.22);
    var activeIndex = 0;
    var linePartial = 0;

    this.stepOffsets.forEach(function (top, i) {
      if (top <= probe) activeIndex = i;
    });

    if (activeIndex < this.stepCount - 1) {
      var currentTop = this.stepOffsets[activeIndex] || 0;
      var nextTop = this.stepOffsets[activeIndex + 1] || (currentTop + 1);
      var span = nextTop - currentTop;
      if (span > 0) {
        linePartial = Math.min(1, Math.max(0, (probe - currentTop) / span));
      }
    } else {
      linePartial = 1;
    }

    if (activeIndex === this.activeIndex && Math.abs(linePartial - this.linePartial) < 0.02) {
      return;
    }

    this.linePartial = linePartial;
    this.applyStepStates(activeIndex, linePartial, {
      fromScroll: true,
      instant: this.reducedMotion
    });
  };

  ProcessSteps.prototype.goToStep = function (index, options) {
    options = options || {};

    if (index < 0 || index >= this.stepCount) return;

    this.measureDesktop();

    if (this.isStickyImageMode()) {
      this.goToStickyImageStep(index, options);
      return;
    }

    var progress = this.getStepProgress(index);
    var linePartial = index < this.stepCount - 1 ? 0 : 1;

    this.isClickNavigating = true;
    clearTimeout(this.clickNavTimer);

    this.applyStepStates(index, linePartial, {
      instant: options.instant || this.reducedMotion,
      fromClick: true
    });

    this.setScrollState(0, progress);
    this.linePartial = linePartial;
    this.scrollZone.classList.toggle('is-pinned', true);

    if (this.maxZonePageScroll > 0) {
      this.updateZoneStartScroll();
      var targetY = this.zoneStartScroll + progress * this.maxZonePageScroll;
      window.scrollTo({
        top: Math.max(0, targetY),
        behavior: 'auto'
      });
    }

    var self = this;
    this.clickNavTimer = setTimeout(function () {
      self.isClickNavigating = false;
      self.syncDesktopFromPage();
    }, 150);
  };

  ProcessSteps.prototype.goToStickyImageStep = function (index, options) {
    options = options || {};

    this.isClickNavigating = true;
    clearTimeout(this.clickNavTimer);

    var linePartial = index < this.stepCount - 1 ? 0 : 1;
    this.applyStepStates(index, linePartial, {
      instant: options.instant || this.reducedMotion,
      fromClick: true
    });
    this.linePartial = linePartial;

    var progress = this.getStickyImageProgressForStep(index);
    var offset = progress * this.maxInternalScroll;
    this.setScrollState(offset, progress);
    this.scrollZone.classList.toggle('is-pinned', true);
    this.updateZoneStartScroll();
    window.scrollTo({
      top: Math.max(0, this.zoneStartScroll + progress * this.maxZonePageScroll),
      behavior: this.reducedMotion || options.instant ? 'auto' : 'smooth'
    });

    var self = this;
    this.clickNavTimer = setTimeout(function () {
      self.isClickNavigating = false;
      self.syncStickyImageFromPage();
    }, this.reducedMotion || options.instant ? 120 : 450);
  };

  ProcessSteps.prototype.updateFromDesktopScroll = function (progress) {
    if (!this.stepElements.length) return;

    progress = typeof progress === 'number' ? progress : 0;

    var activeIndex = 0;
    var linePartial = 0;

    if (this.stepCount > 1) {
      var stepProgress = progress * (this.stepCount - 1);
      activeIndex = Math.min(this.stepCount - 1, Math.floor(stepProgress + 0.0001));
      linePartial = Math.min(1, Math.max(0, stepProgress - activeIndex));
    } else {
      activeIndex = 0;
      linePartial = progress;
    }

    if (activeIndex === this.activeIndex && Math.abs(linePartial - this.linePartial) < 0.01) {
      return;
    }

    this.linePartial = linePartial;
    this.applyStepStates(activeIndex, linePartial, {
      fromScroll: true,
      instant: this.reducedMotion
    });
  };

  ProcessSteps.prototype.applyStepStates = function (index, linePartial, options) {
    options = options || {};
    var fromScroll = options.fromScroll || false;
    var prevIndex = this.activeIndex;

    if (index < 0 || index >= this.stepCount) return;

    this.activeIndex = index;

    this.stepElements.forEach(function (step, i) {
      step.classList.toggle('is-active', i === index);
      step.classList.toggle('is-past', i < index);
      step.classList.toggle('is-lit', i <= index);
    });

    this.dotButtons.forEach(function (btn, i) {
      btn.setAttribute('aria-current', i === index ? 'step' : 'false');
    });

    this.updateLineProgress(linePartial);

    if (!this.isMobile && index !== prevIndex) {
      this.updateDesktopImage(
        index,
        prevIndex,
        fromScroll ? this.reducedMotion : (options.instant || this.reducedMotion),
        options.fromClick || false
      );
    }
  };

  ProcessSteps.prototype.scrollPageToStep = function (index) {
    this.goToStep(index, { instant: this.reducedMotion });
  };

  ProcessSteps.prototype.handleDotClick = function (index) {
    this.goToStep(index, { instant: this.reducedMotion });
  };

  ProcessSteps.prototype.scrollToSlide = function (index) {
    if (!this.slider || !this.slides[index]) return;

    var slide = this.slides[index];
    var sliderRect = this.slider.getBoundingClientRect();
    var slideRect = slide.getBoundingClientRect();
    var padLeft = parseFloat(window.getComputedStyle(this.slider).paddingInlineStart) || 0;
    var left = this.slider.scrollLeft + (slideRect.left - sliderRect.left) - padLeft;

    this.slider.scrollTo({
      left: Math.max(0, left),
      behavior: this.reducedMotion ? 'auto' : 'smooth'
    });

    this.setActiveStep(index, { instant: this.reducedMotion, fromClick: true });
  };

  ProcessSteps.prototype.syncMobileTimeline = function () {
    if (!this.mobileTimelineTrack || !this.slider) return;
    this.mobileTimelineTrack.style.transform = 'translate3d(' + (-this.slider.scrollLeft) + 'px, 0, 0)';
  };

  ProcessSteps.prototype.setActiveStep = function (index, options) {
    options = options || {};

    if (index < 0 || index >= this.stepCount) return;

    if (this.isMobile) {
      this.applyStepStates(index, 0, options);
      this.updateMobileStates(index);
      return;
    }

    if (options.fromClick) {
      this.handleDotClick(index);
      return;
    }

    this.applyStepStates(index, 0, options);
  };

  ProcessSteps.prototype.updateDesktopImage = function (index, prevIndex, instant, fromClick) {
    var incoming = this.imageLayers[index];
    if (!incoming) return;

    var hasImage = incoming.dataset.hasImage === 'true';
    if (!hasImage) {
      this.imageLayers.forEach(function (layer, i) {
        layer.classList.remove('is-visible', 'is-entering');
        layer.setAttribute('aria-hidden', i === index ? 'false' : 'true');
      });
      return;
    }

    var useAlt = fromClick && incoming.dataset.hasAlt === 'true';
    var incomingInner = incoming.querySelector('.process-steps__image-inner');
    var altLayer = incoming.querySelector('.process-steps__image-alt');
    var mainImg = incoming.querySelector('.process-steps__image-main');

    if (useAlt && altLayer) {
      altLayer.hidden = false;
      if (mainImg) mainImg.hidden = true;
    } else {
      if (altLayer) altLayer.hidden = true;
      if (mainImg) mainImg.hidden = false;
    }

    if (index === prevIndex && !fromClick) return;

    var previous = prevIndex !== index ? this.imageLayers[prevIndex] : null;

    if (instant) {
      this.imageLayers.forEach(function (layer, i) {
        layer.classList.remove('is-entering');
        layer.classList.toggle('is-visible', i === index);
        layer.setAttribute('aria-hidden', i === index ? 'false' : 'true');
        var inner = layer.querySelector('.process-steps__image-inner');
        if (inner) inner.style.transform = '';
      });
      return;
    }

    this.imageLayers.forEach(function (layer, i) {
      if (i !== index && i !== prevIndex) {
        layer.classList.remove('is-visible', 'is-entering');
        layer.setAttribute('aria-hidden', 'true');
      }
    });

    if (previous && previous !== incoming) {
      previous.classList.add('is-visible');
      previous.classList.remove('is-entering');
    }

    incoming.classList.remove('is-visible');
    incoming.classList.add('is-entering');
    incoming.setAttribute('aria-hidden', 'false');

    if (incomingInner) {
      incomingInner.style.transform = '';
    }

    var onEnd = function () {
      incoming.classList.remove('is-entering');
      incoming.classList.add('is-visible');
      if (previous && previous !== incoming) {
        previous.classList.remove('is-visible', 'is-entering');
        previous.setAttribute('aria-hidden', 'true');
      }
      if (incomingInner) {
        incomingInner.style.transform = '';
      }
    };

    if (incomingInner && !this.reducedMotion) {
      incomingInner.addEventListener('animationend', onEnd, { once: true });
      setTimeout(onEnd, 800);
    } else {
      onEnd();
    }
  };

  ProcessSteps.prototype.updateMobileStates = function (activeIndex) {
    this.slides.forEach(function (slide, i) {
      slide.classList.toggle('is-active', i === activeIndex);
      slide.classList.toggle('is-past', i < activeIndex);
    });

    this.mobileTimelineItems.forEach(function (item, i) {
      item.classList.toggle('is-active', i === activeIndex);
      item.classList.toggle('is-past', i < activeIndex);
      item.classList.toggle('is-next', i === activeIndex + 1);
    });

    this.mobileDotButtons.forEach(function (btn) {
      var i = parseInt(btn.dataset.stepTrigger, 10);
      btn.setAttribute('aria-selected', i === activeIndex ? 'true' : 'false');
    });
  };

  ProcessSteps.prototype.updateLineProgress = function (activePartial) {
    if (this.isMobile) return;

    var partial = typeof activePartial === 'number' ? activePartial : 0;

    this.stepElements.forEach(function (step, i) {
      var line = step.querySelector('.process-steps__line');
      if (!line) return;

      var lineProgress = 0;
      if (i < this.activeIndex) {
        lineProgress = 1;
      } else if (i === this.activeIndex) {
        lineProgress = partial;
      }

      line.style.setProperty('--progress', lineProgress);
    }, this);
  };

  ProcessSteps.prototype.onSliderScroll = function () {
    if (!this.slider || !this.isMobile || !this.slides.length) return;

    this.syncMobileTimeline();

    var slider = this.slider;
    var sliderRect = slider.getBoundingClientRect();
    var padLeft = parseFloat(window.getComputedStyle(slider).paddingInlineStart) || 0;
    var origin = sliderRect.left + padLeft;
    var activeIndex = 0;
    var minDistance = Infinity;
    var progressBetween = 0;

    this.slides.forEach(function (slide, i) {
      var slideRect = slide.getBoundingClientRect();
      var distance = Math.abs(slideRect.left - origin);
      if (distance < minDistance) {
        minDistance = distance;
        activeIndex = i;
      }
    });

    var current = this.slides[activeIndex];
    var next = this.slides[activeIndex + 1];
    if (current && next) {
      var currentLeft = current.getBoundingClientRect().left;
      var pitch = next.getBoundingClientRect().left - currentLeft;
      if (pitch > 0) {
        progressBetween = Math.min(1, Math.max(0, (origin - currentLeft) / pitch));
      }
    }

    if (activeIndex !== this.activeIndex) {
      this.applyStepStates(activeIndex, progressBetween, { instant: true, fromClick: false });
      this.updateMobileStates(activeIndex);
    }

    this.mobileTimelineItems.forEach(function (item, i) {
      var fill = item.querySelector('.process-steps__mobile-line-fill');
      if (!fill) return;
      var lineProgress = 0;
      if (i < activeIndex) lineProgress = 1;
      else if (i === activeIndex) lineProgress = progressBetween;
      fill.parentElement.style.setProperty('--progress', lineProgress);
    });
  };

  ProcessSteps.prototype.onKeyDown = function (event) {
    if (this.isMobile) return;

    var target = event.target;
    if (!target.classList.contains('process-steps__dot-btn')) return;

    var currentIndex = this.dotButtons.indexOf(target);
    if (currentIndex === -1) return;

    var nextIndex = currentIndex;

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = Math.min(this.dotButtons.length - 1, currentIndex + 1);
      event.preventDefault();
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = Math.max(0, currentIndex - 1);
      event.preventDefault();
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.handleDotClick(currentIndex);
      return;
    } else {
      return;
    }

    if (nextIndex !== currentIndex) {
      this.dotButtons[nextIndex].focus();
      this.handleDotClick(nextIndex);
    }
  };

  ProcessSteps.prototype.onResize = function () {
    var wasMobile = this.isMobile;
    this.isMobile = window.matchMedia(MOBILE_MQ).matches;
    this.reducedMotion = window.matchMedia(REDUCED_MOTION_MQ).matches;

    if (wasMobile !== this.isMobile) {
      this.destroy();
      this.abortController = new AbortController();
      this.init();
    } else if (this.isMobile) {
      this.onSliderScroll();
    } else {
      this.scheduleMeasureDesktop();
    }
  };

  var instances = new WeakMap();

  function initSection(sectionEl) {
    var root = sectionEl.querySelector('.process-steps');
    if (!root) return;

    destroySection(sectionEl);

    var instance = new ProcessSteps(root);
    instance.init();
    instances.set(sectionEl, instance);
  }

  function destroySection(sectionEl) {
    var existing = instances.get(sectionEl);
    if (existing) {
      existing.destroy();
      instances.delete(sectionEl);
    }
  }

  function initAll() {
    document.querySelectorAll('.shopify-section--process-steps').forEach(initSection);
  }

  function handleBlockSelect(event) {
    var block = event.target;
    var sectionEl = block.closest('.shopify-section--process-steps');
    if (!sectionEl) return;

    var instance = instances.get(sectionEl);
    if (!instance) return;

    if (!instance.isMobile) {
      instance.scheduleMeasureDesktop();
    }

    var step = block.closest('[data-step-index]');
    if (!step) return;

    var index = parseInt(step.dataset.stepIndex, 10);
    if (isNaN(index)) return;

    if (!instance.isMobile) {
      instance.handleDotClick(index);
    } else {
      instance.scrollToSlide(index);
    }
  }

  function handleSectionEvent(event) {
    var sectionEl = event.target;
    if (!sectionEl.classList || !sectionEl.classList.contains('shopify-section--process-steps')) return;

    initSection(sectionEl);

    var instance = instances.get(sectionEl);
    if (instance && !instance.isMobile) {
      setTimeout(function () {
        instance.scheduleMeasureDesktop();
      }, 300);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  window.addEventListener('load', function () {
    document.querySelectorAll('.shopify-section--process-steps').forEach(function (sectionEl) {
      var instance = instances.get(sectionEl);
      if (instance && !instance.isMobile) {
        instance.scheduleMeasureDesktop();
      }
    });
  });

  document.addEventListener('shopify:section:load', handleSectionEvent);
  document.addEventListener('shopify:section:reorder', handleSectionEvent);
  document.addEventListener('shopify:section:select', handleSectionEvent);
  document.addEventListener('shopify:block:select', handleBlockSelect);

  document.addEventListener('shopify:section:unload', function (event) {
    if (event.target.classList.contains('shopify-section--process-steps')) {
      destroySection(event.target);
    }
  });
})();
